import {
  faComment,
  faPencilAlt,
  faReply,
  faShare
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Avatar from 'components/common/Avatar';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import DialogModal from 'components/modals/DialogModal';
import TicketDescriptionEditModal from 'components/modals/TicketDescriptionEditModal';
import AddNoteComponent from 'components/offcanvas/AddNoteOffcanvas';
import ForwardNoteComponent from 'components/offcanvas/ForwardNoteOffcanvas';
import ReplyNoteComponent from 'components/offcanvas/ReplyNoteOffcanvas';
import FailedRequest from 'components/requests-response/FailedRequest';
import SuccessRequest from 'components/requests-response/SuccessRequest';
import DOMPurify from 'dompurify';
import useApi from 'helpers/api';
import { formatDateInFromNow } from 'helpers/utils';
import { useAuthenticatedUser } from 'hooks/useAuthenticatedUser';
import useCommunication from 'hooks/useCommunication';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { Button } from 'react-bootstrap';
import AgentPhotoService from 'services/AgentPhoto.service';
import TicketsService from 'services/Tickets.service';

const TicketInfo = forwardRef(({ data, refreshData, refreshLogs }, ref) => {
  const api = useApi();
  const { authenticatedUser } = useAuthenticatedUser();
  const divDescription = useRef(null);
  const notesContainerRef = useRef(null);
  const componentRef = useRef(null);

  const [notes, setNotes] = useState([]);
  const [userPhotos, setUserPhotos] = useState({});
  const [currentUserPhoto, setCurrentUserPhoto] = useState(null);
  const [error, setError] = useState({});
  const [selectedNote, setSelectedNote] = useState({});

  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const sanitizedHtml = DOMPurify.sanitize(data.description);
  const isDisabledForm =
    (data.status &&
      (data.status.status == 'COMPLETED' || data.status.status == 'CLOSED')) ||
    data.deleted;
  const isClosed =
    (data.status &&
      (data.status.status === 'COMPLETED' ||
        data.status.status === 'CLOSED')) ||
    data.deleted;

  // Usando o hook compartilhado
  const {
    activeComponent,
    closeComponent,
    showAddNote,
    showReplyNote,
    showForwardNote
  } = useCommunication();

  useEffect(() => {
    getNotes();
  }, [data.id]);

  useEffect(() => {
    fetchUserPhotos();
  }, [notes]);

  useEffect(() => {
    if (authenticatedUser?.id) {
      fetchCurrentUserPhoto(authenticatedUser.id);
    }
  }, [authenticatedUser]);

  useEffect(() => {
    // Aplicar estilos nas imagens da descrição
    applyImageStyles(divDescription.current);

    // Configurar links para abrir em nova aba
    const links = divDescription.current?.querySelectorAll('a');
    if (links) {
      links.forEach(link => {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      });
    }
  }, [data.description]);

  useEffect(() => {
    // Aplicar estilos nas imagens das notas
    if (notes.length > 0 && notesContainerRef.current != null) {
      applyImageStyles(notesContainerRef.current);
    }
  }, [notes]);

  // Efeito para rolar até o componente quando ele é ativado
  useEffect(() => {
    if (activeComponent && componentRef.current) {
      setTimeout(() => {
        componentRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }, [activeComponent]);

  useImperativeHandle(ref, () => ({
    getNotes: () => getNotes()
  }));

  const fetchCurrentUserPhoto = async userId => {
    if (!userId) return;

    try {
      const request = await AgentPhotoService.find(userId);
      if (request.ok) {
        const response = await request.json();
        setCurrentUserPhoto(response.url);
      } else {
        setCurrentUserPhoto(null); // Reset photo if not found
      }
    } catch (error) {
      console.log(`Foto não encontrada para o usuário ${userId}`);
      setCurrentUserPhoto(null);
    }
  };

  const fetchUserPhotos = async () => {
    if (!notes || notes.length === 0) return;

    const userIds = [
      ...new Set(notes.map(note => note.user?.id).filter(id => id))
    ];

    if (userIds.length > 0) {
      // Filtrar IDs que já temos em cache para evitar requisições duplicadas
      const idsToFetch = userIds.filter(id => !userPhotos[id]);

      if (idsToFetch.length === 0) return;

      const requests = idsToFetch.map(id => AgentPhotoService.find(id));
      const responses = await Promise.all(requests);
      const newPhotos = { ...userPhotos };

      for (let i = 0; i < responses.length; i++) {
        if (responses[i].ok) {
          const { url } = await responses[i].json();
          newPhotos[idsToFetch[i]] = url;
        }
      }
      setUserPhotos(newPhotos);
    }
  };

  // Função utilitária para aplicar estilos nas imagens
  const applyImageStyles = container => {
    if (!container) return;

    const imgs = container.querySelectorAll('img');
    imgs.forEach(img => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.maxHeight = '400px';
      img.style.objectFit = 'contain';
      img.style.display = 'block';
      img.style.margin = '10px auto';
      img.style.borderRadius = '8px';
      img.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
      img.style.overflow = 'hidden';
    });
  };

  const editDescription = async body => {
    setIsLoading(true);
    const response = await TicketsService.edit(data.id, body);
    if (response.status) {
      refreshData();
      refreshLogs();
    } else {
      setError(response);
      setShowError(true);
    }
    setIsLoading(false);
    handleCloseModalDescription();
  };

  const getNotes = async () => {
    if (data.id != undefined) {
      let response = await api.ticketNotes.list(data.id);
      if (response) setNotes(response);
    }
  };

  const deleteNote = async response => {
    if (response) {
      let response = await api.ticketNotes.delete(data.id, selectedNote.id);
      if (response.ok) {
        setShowSuccess(true);
        refreshLogs();
        getNotes();
      } else {
        setError(response);
        setShowError(true);
      }
      setShowDialog(false);
      setSelectedNote({});
    }
  };

  const handleAddNote = () => {
    setSelectedNote({}); // Limpa nota selecionada
    showAddNote(); // Usa função do hook
  };

  const handleEditNote = item => {
    setSelectedNote(item);
    showAddNote(); // Usa função do hook
  };

  const handleDeleteNote = item => {
    setSelectedNote(item);
    setShowDialog(true);
  };

  const handleCloseModalDescription = () => {
    setShowModal(false);
  };

  const getNoteIcon = type => {
    switch (type) {
      case 'NOTE':
        return faComment;
      case 'REPLY':
        return faReply;
      case 'FORWARD':
        return faShare;
    }
  };

  const getNoteViewDescription = type => {
    switch (type) {
      case 'PUBLIC':
        return 'público';
      case 'PRIVATE':
        return 'privado';
    }
  };

  const getNoteDescription = type => {
    switch (type) {
      case 'NOTE':
        return 'adicionou uma nota';
      case 'REPLY':
        return 'respondeu';
      case 'FORWARD':
        return 'encaminhou';
    }
  };

  const renderActionButtons = () => {
    const getCurrentUserName = () => {
      if (!authenticatedUser) return 'Usuário';
      return `${authenticatedUser.firstName} ${
        authenticatedUser.lastName || ''
      }`.trim();
    };

    return (
      <footer className="px-2 py-3 mb-3 bg-100 rounded-3">
        <Flex alignItems="center">
          <div className="me-3">
            <Avatar
              name={getCurrentUserName()}
              size="2xl"
              rounded="circle"
              src={currentUserPhoto}
            />
          </div>

          <div className="flex-1">
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="reply"
              transform="shrink-2"
              iconAlign="middle"
              onClick={showReplyNote}
              className="me-2"
              disabled={isClosed || activeComponent !== null}
            >
              <span className="d-none d-md-inline-block ms-1">Responder</span>
            </IconButton>

            <IconButton
              variant="falcon-default"
              size="sm"
              icon="file-alt"
              transform="shrink-2"
              iconAlign="middle"
              onClick={handleAddNote}
              className="me-2"
              disabled={isClosed || activeComponent !== null}
            >
              <span className="d-none d-md-inline-block ms-1">
                Adicionar nota
              </span>
            </IconButton>

            <IconButton
              variant="falcon-default"
              size="sm"
              icon="share"
              transform="shrink-2"
              iconAlign="middle"
              onClick={showForwardNote}
              className="me-2"
              disabled={isClosed || activeComponent !== null}
            >
              <span className="d-none d-md-inline-block ms-1">Encaminhar</span>
            </IconButton>
          </div>
        </Flex>
      </footer>
    );
  };

  return (
    <>
      <TicketDescriptionEditModal
        showModal={showModal}
        data={data.description}
        handleClose={handleCloseModalDescription}
        handleSubmit={editDescription}
        isLoading={isLoading}
        ticketId={data.id}
      />
      <DialogModal
        showModal={showDialog}
        handleClose={() => setShowDialog(false)}
        message="Tem certeza que deseja excluir a nota?"
        response={deleteNote}
      />

      <FailedRequest
        message={error.description}
        showAlert={showError}
        setShowAlert={() => setShowError(false)}
      />

      <SuccessRequest
        message="Nota excluída com sucesso"
        showAlert={showSuccess}
        setShowAlert={() => setShowSuccess(false)}
      />

      <div className="d-flex flex-column">
        <TicketDescriptionEditModal
          showModal={showModal}
          data={data.description}
          handleClose={handleCloseModalDescription}
          handleSubmit={editDescription}
          ticketId={data.id}
        />
        <div>
          <div className="d-flex align-items-center">
            <h6 style={{ fontSize: 16 }}>Descrição</h6>
            <Button
              size="sm"
              variant="link"
              className="text-600 py-0 ms-1 pb-2"
              onClick={() => setShowModal(true)}
            >
              <FontAwesomeIcon icon={faPencilAlt} size="sm" />
            </Button>
          </div>
          <div
            ref={divDescription}
            style={{
              textAlign: 'justify',
              overflow: 'hidden',
              wordWrap: 'break-word',
              maxWidth: '100%'
            }}
            className="pe-3 w-100 overflow-x-hidden rich-text-content mb-5"
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />

          <div ref={notesContainerRef}>
            {notes.map((item, i) => {
              const isNote = item.typeNote === 'NOTE';
              const isPrivateNote = item.typeView === 'PRIVATE';

              const userName = item.user
                ? item.user.firstName
                : item.contactCustomer
                ? item.contactCustomer.name
                : item.sender
                ? item.sender.split('<')[0].trim()
                : 'Sistema';

              const displayName = item.user
                ? item.user.firstName + ' ' + item.user.lastName
                : item.contactCustomer
                ? `${item.contactCustomer.name} <${item.contactCustomer.email}>`
                : item.sender;

              const avatarSrc = item.user ? userPhotos[item.user.id] : null;

              return (
                <div key={i} className="d-flex mb-4">
                  <div className="me-3">
                    <Avatar
                      name={userName}
                      size="2xl"
                      rounded="circle"
                      src={avatarSrc}
                    />
                  </div>
                  <div className="flex-1">
                    <div
                      className={classNames('p-3 rounded-3 border shadow-sm', {
                        'bg-warning-subtle border-warning-subtle':
                          isPrivateNote,
                        'bg-100 border-200': !isPrivateNote
                      })}
                    >
                      <Flex
                        justifyContent="between"
                        alignItems="start"
                        className="mb-2"
                      >
                        <div className="flex-1">
                          <h6 className="mb-0 fw-bold text-900">
                            {displayName}
                            <span className="text-500 fw-normal ms-2 fs--1">
                              <FontAwesomeIcon
                                icon={getNoteIcon(item.typeNote)}
                                className="me-1"
                              />
                              {getNoteDescription(item.typeNote)}
                              {isNote &&
                                ` • ${getNoteViewDescription(item.typeView)}`}
                            </span>
                          </h6>
                          <small className="text-600">
                            {formatDateInFromNow(item.date)}
                          </small>
                          {!isNote && (
                            <div className="fs--1 text-600 mt-1">
                              <strong>Para: </strong>
                              {item.recipients}
                              {item.typeNote === 'REPLY' && item.cc && (
                                <span className="ms-2">
                                  <strong>CC: </strong>
                                  {item.cc}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="ms-2">
                          {isNote && (
                            <Flex>
                              <IconButton
                                size="sm"
                                variant="link"
                                icon="pencil-alt"
                                className="p-0 text-600 me-2"
                                onClick={() => handleEditNote(item)}
                              />
                              <IconButton
                                size="sm"
                                variant="link"
                                icon="trash-alt"
                                className="p-0 text-danger"
                                onClick={() => handleDeleteNote(item)}
                              />
                            </Flex>
                          )}
                        </div>
                      </Flex>

                      <div
                        className="note-content text-900"
                        style={{
                          wordWrap: 'break-word',
                          overflow: 'hidden',
                          maxWidth: '100%'
                        }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(item.note)
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {renderActionButtons()}

          {/* Componentes de comunicação renderizados apenas aqui */}
          {activeComponent && (
            <div ref={componentRef} className="mb-0">
              {activeComponent === 'add' && (
                <AddNoteComponent
                  refreshData={refreshData}
                  refreshLogs={refreshLogs}
                  refreshNotes={getNotes}
                  show={true}
                  handleClose={closeComponent}
                  data={selectedNote}
                  ticket={data}
                />
              )}
              {activeComponent === 'reply' && (
                <ReplyNoteComponent
                  refreshData={refreshData}
                  refreshLogs={refreshLogs}
                  refreshNotes={getNotes}
                  show={true}
                  handleClose={closeComponent}
                  ticketId={data.id}
                  customerId={data.customer.id}
                  contact={data.contactCustomer}
                  ticketStatus={data.status}
                />
              )}
              {activeComponent === 'forward' && (
                <ForwardNoteComponent
                  refreshLogs={refreshLogs}
                  refreshNotes={getNotes}
                  show={true}
                  handleClose={closeComponent}
                  ticketId={data.id}
                  ticketSubject={data.subject}
                  ticketDescription={data.description}
                  customerId={data.customer.id}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
});

export default TicketInfo;
